import { useEffect, useMemo } from 'react';
import { NetworkStatus, useQuery } from '@apollo/client';
import { keyBy } from 'lodash-es';
import {
    GetProjectCatalogDocument,
    type GetProjectCatalogQuery,
    type GetProjectCatalogQueryVariables,
} from '@/graphql/types';
import type { UseOrderAndPaginationControlReturn } from '../../../table/hooks';
import { createAfterCursor, extractNodes } from '../../../../utils/relay';
import type { ProjectOverviewRow } from '../types';
import type { ProjectSummary } from './projectSummary';

interface UseProjectCatalogQueryArgs {
    archived: boolean;
    search: string;
    orderAndPaginationControl: UseOrderAndPaginationControlReturn;
    enabled?: boolean;
}

const NO_PROJECT_SUMMARIES: Record<string, ProjectSummary> = {};

export function useProjectCatalogQuery({
    archived,
    search,
    orderAndPaginationControl,
    enabled = true,
}: UseProjectCatalogQueryArgs) {
    const { pageSize, currentPage, orderBy, setTotalCount } = orderAndPaginationControl;

    const queryVariables = useMemo(
        () => ({
            archived,
            search: search.trim() || undefined,
            orderBy: orderBy || undefined,
            first: pageSize,
            after: createAfterCursor(currentPage, pageSize),
        }),
        [archived, search, orderBy, pageSize, currentPage]
    );

    const { data, previousData, loading, error, networkStatus } = useQuery<
        GetProjectCatalogQuery,
        GetProjectCatalogQueryVariables
    >(GetProjectCatalogDocument, {
        variables: queryVariables,
        skip: !enabled,
        // Cross-tab archive/overview transitions can otherwise reuse stale per-tab cached pages.
        // Example: archive list visited before an overview-side archive action.
        // Keep cache for instant paint, but always refresh from network.
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    useEffect(() => {
        if (!enabled) {
            return;
        }

        // Avoid transient count resets while variables are changing.
        // Keep the previous count until the current request settles.
        if (loading) {
            return;
        }

        const totalCount = data?.projectCatalog?.totalCount;
        setTotalCount(typeof totalCount === 'number' ? totalCount : 0);
    }, [data?.projectCatalog?.totalCount, enabled, loading, setTotalCount]);

    const projectRows = useMemo<ProjectOverviewRow[]>(
        () =>
            extractNodes(data?.projectCatalog?.edges).map((node) => ({
                projectRelayId: node.id,
                projectId: node.pk,
                projectName: node.name,
                isStarred: node.isStarred,
                city: node.city,
                swarmType: node.swarmType,
                archived: node.archived,
                permission: node.permission,
                measuringPointCount: node.measuringPointCount,
            })),
        [data?.projectCatalog?.edges]
    );

    const summaries = data?.projectCatalog?.summaries;
    const projectSummariesByProjectId = useMemo(
        () => (summaries ? keyBy(summaries, 'projectId') : NO_PROJECT_SUMMARIES),
        [summaries]
    );

    return {
        loading: loading && networkStatus === NetworkStatus.loading,
        updating: loading && networkStatus === NetworkStatus.setVariables,
        error,
        starredProjects: data?.projectCatalog
            ? {
                  count: data.projectCatalog.starredCount,
                  limit: data.projectCatalog.maxStarredProjects,
              }
            : undefined,
        projectRows,
        projectSummariesByProjectId,
        // Not tied to the filters, so the previous page's value holds while the next one loads.
        // Otherwise a search or page change would flash the empty state.
        hasAnyData: Boolean((data ?? previousData)?.projectCatalog?.hasAnyData),
    };
}
